<script setup lang="ts">
import { cn } from "@voltalis/frontend-utils";
import { getCurrentInstance, inject, onBeforeUnmount, onMounted, ref, watch } from "vue";

import { IWizardTabExpose, IWizardTabProps } from "@/components/ui/wizard/wizard-tab.types";
import { EWizardCloseState, IWizardInject, WizardInject } from "@/components/ui/wizard/wizard.types";
import { useId } from "@/composables/utils/use-id";
import { useTypedIntl } from "@/locales/typed-intl";
import { TComponent } from "@/utils/component-utils";

import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";
import WizardTab from "@/components/ui/wizard/wizard-tab.vue";

interface IEmits {
  (e: "activated"): void;
}

const props = withDefaults(defineProps<IWizardTabProps>(), {
  lazy: false,
  title: "",
  hideBack: false,
  validate: () => true,
});

const emits = defineEmits<IEmits>();

const wizard = inject<IWizardInject>(WizardInject);
const id = useId();
const { t, k } = useTypedIntl();

const active = ref(false);

async function handleFinishWizard() {
  if (props.beforeFinishWizardCb) {
    const result = await props.beforeFinishWizardCb();
    if (!result) return;
  }

  wizard?.finishWizard();
}

onMounted(() => {
  wizard?.addTab(instance as unknown as TComponent<typeof WizardTab, IWizardTabProps, IWizardTabExpose>);
});

onBeforeUnmount(() => {
  wizard?.removeTab(instance as unknown as TComponent<typeof WizardTab, IWizardTabProps, IWizardTabExpose>);
});

const instance = getCurrentInstance();

watch(active, (nextActive) => {
  if (nextActive && props.hideBack) {
    wizard?.hideBackButton();

    return;
  }

  if (!nextActive) {
    wizard?.showBackButton();
  }
});

defineExpose<IWizardTabExpose>({
  setId(nextId: string) {
    id.value = nextId;
  },
  setActive(isActive: boolean) {
    active.value = isActive;
    if (isActive) {
      emits("activated");
    }
  },
  validate() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const childrenExposedValidate = (instance as any)?.subTree?.children[0]?.children[0]?.component?.exposed?.validate;
    if (childrenExposedValidate && typeof childrenExposedValidate === "function") {
      return childrenExposedValidate();
    }

    return props.validate();
  },
});
</script>

<template>
  <div
    v-if="!props.lazy || active"
    v-show="active"
    :id="id"
    :aria-hidden="!active"
    :aria-labelledby="`step-${id}`"
    class="wizard-tab-container"
    role="tabpanel"
  >
    <slot
      ref="child"
      :active="active"
    />

    <div class="flex flex-row items-center justify-center w-full gap-[8px] mt-8">
      <span
        v-for="tabIdx in props.tabsLength"
        :key="tabIdx"
        class="w-[10px] h-[10px] bg-blue-100 rounded-full"
        :class="{
          'bg-brand': tabIdx === props.currentIdx + 1,
        }"
      />
    </div>

    <div class="wizard-tab-footer w-full flex flex-col gap-2 mt-6">
      <slot
        name="footer"
        :focusNextTab="wizard?.focusNextTab"
        :focusPrevTab="wizard?.focusPrevTab"
      >
        <div class="w-full flex flex-row gap-2">
          <template v-if="!props.hideBack">
            <HospitalityButton
              v-if="props.currentIdx === 0"
              class="flex flex-1"
              :label="t(k.global.misc.cancel)"
              severity="secondary"
              @click="wizard?.closeWizard(EWizardCloseState.ABANDONED)"
            />
            <HospitalityButton
              v-else
              class="flex flex-1"
              :disabled="props.disablePrevious"
              :label="t(k.global.misc.back)"
              severity="secondary"
              @click="wizard?.focusPrevTab()"
            />
          </template>
          <HospitalityButton
            v-if="props.currentIdx !== props.tabsLength - 1 && !props.hideNext"
            :class="
              cn('flex flex-1', {
                'button-block': props.hideBack,
              })
            "
            :disabled="props.disableNext || props.loadingNextBtn"
            :label="t(k.global.misc.next)"
            :loading="props.loadingNextBtn"
            @click="wizard?.focusNextTab()"
          />
          <HospitalityButton
            v-else-if="!props.hideNext"
            :class="
              cn('flex flex-1', {
                'button-block': props.hideBack,
              })
            "
            :disabled="props.disableNext || props.loadingNextBtn"
            :label="props.nextBtnLabel ?? t(k.global.misc.close)"
            :loading="props.loadingNextBtn"
            @click="handleFinishWizard"
          />
        </div>
      </slot>
    </div>
  </div>
</template>

<style scoped lang="scss"></style>
